const ATP_CACHE_TTL_MS = 30000;
// TPS-641: Abort STORIS ATP requests that exceed this duration.
const STORIS_ATP_TIMEOUT_MS = 15000;
const atpPendingRequests = new Map();
const atpResponseCache = new Map();

function normalizeBooleanValue(value) {
  return value === true || value === "true";
}

function getCallForAvailabilityMessages() {
  return {
    nodeText: `Call for Availability <a href="tel:+18004570305">(800) 457-0305</a>`,
    cartLineItemAvailabilityMessage: "Call for Availability (800) 457-0305",
  };
}

function buildAtpLookupConditions({
  isCustom,
  variantSku,
  itemQuantity,
  availableQty,
  variantLeadTime,
}) {
  const normalizedItemQuantity = Number(itemQuantity) || 0;
  const normalizedAvailableQty = Number.isFinite(Number(availableQty))
    ? Number(availableQty)
    : 0;
  const normalizedIsCustom = normalizeBooleanValue(isCustom);
  const normalizedVariantSku = variantSku || "";
  const hasSku = normalizedVariantSku.trim() !== "";
  const quantityExceedsAvailableQty =
    normalizedItemQuantity > normalizedAvailableQty;
  // Matches getProductAvailabilityMessages ATP branch: not explicitly custom
  // (normalizeBooleanValue), non-empty SKU, line qty exceeds reported available qty.
  const shouldCallGetATPDate =
    !normalizedIsCustom && hasSku && quantityExceedsAvailableQty;

  return {
    isCustom,
    variantSku: normalizedVariantSku,
    itemQuantity: normalizedItemQuantity,
    availableQty: normalizedAvailableQty,
    variantLeadTime: variantLeadTime || "",
    hasSku,
    quantityExceedsAvailableQty,
    shouldCallGetATPDate,
  };
}

async function getATPDate({ sku, quantity, forceRefresh = false }) {
  // ETA/TPS-614: dedupe in-flight ATP lookups and short-cache results to avoid
  // repeated identical network calls during UI churn.
  const cacheKey = `${sku || ""}:${quantity || 0}`;
  const now = Date.now();

  if (!forceRefresh) {
    const cached = atpResponseCache.get(cacheKey);
    if (cached && now - cached.timestamp <= ATP_CACHE_TTL_MS) {
      return cached.value;
    }
  }

  const inFlight = atpPendingRequests.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const requestPromise = (async () => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), STORIS_ATP_TIMEOUT_MS);

    try {
      const response = await fetch(
        "https://todays-patio.endertech.net/api/product-delivery-date",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization:
              "Bearer 632E64A997A6E8722122FF46D8012AB79550051CAE20A6E785E2BE225685CE0B",
            Origin: "https://todays-patio.endertech.net",
          },
          body: `sku=${sku}&quantity=${quantity}`,
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error("error response");
      }

      const responseObj = await response?.json();

      if (responseObj?.deliveryDate === null) {
        atpResponseCache.set(cacheKey, { value: null, timestamp: Date.now() });
        return null;
      }

      const deliveryDate = new Date(responseObj?.deliveryDate);

      let deliveryDatePadded = new Date(deliveryDate.getTime());
      // TPS-654 Remove lead time buffer from ATP date.
      // deliveryDatePadded.setDate(deliveryDatePadded.getDate() + 5);

      deliveryDatePadded.setDate(
        deliveryDatePadded.getDate() + ((8 - deliveryDatePadded.getDay()) % 7)
      );

      const estimateDate = new Intl.DateTimeFormat("en-US", {
        timeZone: "GMT",
        month: "long",
        day: "2-digit",
      }).format(deliveryDatePadded);

      atpResponseCache.set(cacheKey, { value: estimateDate, timestamp: Date.now() });
      return estimateDate;
    } catch (error) {
      if (error?.name === "AbortError") {
        console.error(
          "STORIS ATP lookup timed out and could not retrieve delivery date",
          { sku, quantity }
        );
        return null;
      }

      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  })();

  atpPendingRequests.set(cacheKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    atpPendingRequests.delete(cacheKey);
    if (atpResponseCache.size > 100) {
      atpResponseCache.clear();
    }
  }
}

async function getProductAvailabilityMessages(
  variantSku,
  itemQuantity,
  availableQty,
  variantLeadTime,
  isCustom,
  options = {}
) {
  const { forceRefresh = false } = options;
  let cartLineItemAvailabilityMessage = "";
  let nodeText = "";
  const callForAvailability = getCallForAvailabilityMessages();

  if (availableQty <= 0) {
    if (variantLeadTime) {
      if (isCustom === true || isCustom === "true") {
        nodeText = `Custom order. Ships in ${variantLeadTime}.`;
      } else {
        nodeText = `Ships in ${variantLeadTime}.`;
      }
    } else {
      if (isCustom === true || isCustom === "true") {
        nodeText = "Custom order. Ships in 4-8 weeks.";
      } else {
        nodeText = callForAvailability.nodeText;
        cartLineItemAvailabilityMessage = callForAvailability.cartLineItemAvailabilityMessage;
      }
    }
    cartLineItemAvailabilityMessage = cartLineItemAvailabilityMessage
      ? cartLineItemAvailabilityMessage
      : nodeText;
  } else {
    if (variantLeadTime) {
      nodeText =
        availableQty +
        " in stock. Ships in " +
        variantLeadTime +
        ".<br/>Local pickup also available at checkout.";
    } else {
      nodeText =
        availableQty +
        " in stock. Ships in 3-5 days.<br/>Local Pickup also available at checkout.";
    }
    cartLineItemAvailabilityMessage = "In Stock";
  }

  const skuForAtp = String(variantSku || "").trim();
  const hasSkuForAtp = skuForAtp !== "";
  if (!normalizeBooleanValue(isCustom) && hasSkuForAtp) {
    if (itemQuantity > availableQty) {
      cartLineItemAvailabilityMessage = "";
      try {
        const estimateDate = await window.getATPDate({
          sku: skuForAtp,
          quantity: itemQuantity,
          forceRefresh,
        });
        if (estimateDate) {
          nodeText = "Available the week of " + estimateDate + ".";
          cartLineItemAvailabilityMessage = nodeText;
        } else {
          nodeText = callForAvailability.nodeText;
          cartLineItemAvailabilityMessage = callForAvailability.cartLineItemAvailabilityMessage;
        }
      } catch (error) {
        console.error("Error fetching ATP date: ", error);
        nodeText = callForAvailability.nodeText;
        cartLineItemAvailabilityMessage = callForAvailability.cartLineItemAvailabilityMessage;
      }
    }
  }

  return {
    cartLineItemAvailabilityMessage,
    nodeText,
  };
}

// TPS-631: Read live variant inventory from the Liquid cart map (request-time).
function getLiveVariantInventoryQuantity(variantId) {
  const map = window.tpsCartVariantInventory;
  if (!map || variantId == null || variantId === "") {
    return null;
  }

  const qty = Number(map[String(variantId)]);
  return Number.isFinite(qty) ? qty : null;
}

window.tpsAtpLookup = {
  buildAtpLookupConditions,
};

window.tpsCallForAvailabilityMessages = getCallForAvailabilityMessages;
window.tpsGetLiveVariantInventoryQuantity = getLiveVariantInventoryQuantity;
window.getATPDate = getATPDate;
window.getProductAvailabilityMessages = getProductAvailabilityMessages;
